import { useCallback } from "react";
import dagre from "dagre";

const NODE_WIDTH = 260;
const NODE_HEIGHT = 120;

/**
 * Hook que fornece layout automatico usando dagre.
 * Retorna uma funcao que recebe nodes/edges atuais e retorna
 * novos nodes com posicoes calculadas.
 */
// Tipos de node que sao apenas visuais — nao participam do layout automatico
const VISUAL_ONLY_TYPES = ["group", "stickyNote"];

export default function useAutoLayout() {
  const getLayoutedElements = useCallback((nodes, edges, direction = "LR") => {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));

    // LR = esquerda-para-direita (fluxo horizontal), TB = cima-para-baixo
    g.setGraph({
      rankdir: direction,
      nodesep: 60,
      ranksep: 100,
      edgesep: 30,
      marginx: 40,
      marginy: 40,
    });

    // Separa nodes visuais (group, stickyNote) dos nodes de fluxo
    const flowNodes = nodes.filter((n) => !VISUAL_ONLY_TYPES.includes(n.type));
    const visualNodes = nodes.filter((n) => VISUAL_ONLY_TYPES.includes(n.type));

    // Adiciona apenas nodes de fluxo ao grafo dagre
    flowNodes.forEach((node) => {
      const w = node.measured?.width || node.width || NODE_WIDTH;
      const h = node.measured?.height || node.height || NODE_HEIGHT;
      g.setNode(node.id, { width: w, height: h });
    });

    // Adiciona edges ao grafo dagre
    edges.forEach((edge) => {
      g.setEdge(edge.source, edge.target);
    });

    // Executa o layout
    dagre.layout(g);

    // Aplica posicoes calculadas (dagre retorna centro, React Flow usa canto superior esquerdo)
    const layoutedFlowNodes = flowNodes.map((node) => {
      const nodeWithPosition = g.node(node.id);
      if (!nodeWithPosition) return node;

      const w = node.measured?.width || node.width || NODE_WIDTH;
      const h = node.measured?.height || node.height || NODE_HEIGHT;

      return {
        ...node,
        position: {
          x: nodeWithPosition.x - w / 2,
          y: nodeWithPosition.y - h / 2,
        },
      };
    });

    // Retorna nodes de fluxo reorganizados + nodes visuais na posicao original
    return [...layoutedFlowNodes, ...visualNodes];
  }, []);

  return { getLayoutedElements };
}
